import { FC, ReactNode } from 'react';
import styles from './KursBlock.module.css';
import { Button, BodyText } from '@kpa/kul';
import { ContentResponse } from '../../types/shared';
import { KursBlockResponse } from '../../types/blocks';

type KursBlockProps = {
  contentResponse: ContentResponse;
};

const KursBlock: FC<KursBlockProps> = ({ contentResponse }): ReactNode => {
  const kursBlockResponse = contentResponse.content as KursBlockResponse;

  function formatKursDatum(datumFrån: Date | undefined, datumTill: Date | undefined): string {
    if (!datumFrån || !datumTill) {
      return '';
    }

    const kursDatumFrån = new Date(datumFrån);
    const kursDatumTill = new Date(datumTill);
    const dagKursDatumFrån = kursDatumFrån.getDate();
    const månadKursDatumFrån = kursDatumFrån.toLocaleString('default', { month: 'long' });
    const startTimmar = kursDatumFrån.getHours();
    const startMinuter = kursDatumFrån.getMinutes().toString().padStart(2, '0');
    const slutTimmar = kursDatumTill.getHours();
    const slutMinuter = kursDatumTill.getMinutes().toString().padStart(2, '0');
    const slåIhopDatumOchTid = `${dagKursDatumFrån} ${månadKursDatumFrån} kl ${startTimmar}.${startMinuter}`;

    if (kursDatumTill.getDay() !== kursDatumFrån.getDay()) {
      const dagKursDatumTill = kursDatumTill.getDate();
      const månadKursDatumTill = kursDatumFrån.toLocaleString('default', { month: 'long' });
      return `${slåIhopDatumOchTid} - ${dagKursDatumTill} ${månadKursDatumTill} kl ${slutTimmar}.${slutMinuter}`;
    }

    return `${slåIhopDatumOchTid} - ${slutTimmar}.${slutMinuter}`;
  }

  function formatSistaAnmälningsdagDag(sistaAnmälningsdag: Date | undefined): string {
    if (!sistaAnmälningsdag) {
      return '';
    }

    const sistaAnmälningsdagDatum = new Date(sistaAnmälningsdag);
    const dagSistaAnmälningsDatum = sistaAnmälningsdagDatum.getDate();
    const månadSistaAnmälningsDatum = sistaAnmälningsdagDatum.toLocaleString('default', { month: 'long' });

    return `${dagSistaAnmälningsDatum} ${månadSistaAnmälningsDatum}`;
  }

  function kontrolleraDatum(sistaAnmälningsdag: Date | undefined): boolean {
    if (!sistaAnmälningsdag) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(sistaAnmälningsdag);
    compareDate.setHours(0, 0, 0, 0);
    return today <= compareDate;
  }

  return (
    <div className={styles.kursBlock} aria-label="Information om kursen">
      <div className={styles.målgrupp} aria-label="Målgrupp">
        <BodyText size="text" className={styles.titel}>Målgrupp</BodyText>
        <BodyText size="textBold">{kursBlockResponse.målgrupp}</BodyText>
      </div>

      <div className={styles.inutiKursBlock}>
        <div className={styles.datum} aria-label="Datum">
          <BodyText size="text" className={styles.titel}>Datum</BodyText>
          <BodyText size="textBold">{formatKursDatum(kursBlockResponse.datumFrån, kursBlockResponse.datumTill)}</BodyText>
        </div>

        <div className={styles.plats} aria-label="Plats">
          <BodyText size="text" className={styles.titel}>Plats</BodyText>
          <BodyText size="textBold">{kursBlockResponse.plats}</BodyText>
        </div>

        <div className={styles.pris} aria-label="Pris">
          <BodyText size="text" className={styles.titel}>Pris</BodyText>
          <BodyText size="textBold">{kursBlockResponse.pris}</BodyText>
        </div>

        <div className={styles.anmälningsdag} aria-label="Sista anmälningsdag">
          <BodyText size="text" className={styles.titel}>Sista anmälningsdag</BodyText>
          <BodyText size="textBold">{formatSistaAnmälningsdagDag(kursBlockResponse.sistaAnmälningsdag)}</BodyText>
        </div>
      </div>

      {kontrolleraDatum(kursBlockResponse.sistaAnmälningsdag) && kursBlockResponse.urlHref !== null && (
        <Button
          aria-label={kursBlockResponse.urlTitle}
          href={kursBlockResponse.urlHref}
          target={kursBlockResponse.urlTarget ?? undefined}
          className={styles.button}
        >
          <BodyText size="text">{kursBlockResponse.urlText}</BodyText>
        </Button>
      )}
    </div>
  );
};

export default KursBlock;
